import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  card,
  cardTitle,
  bodySecondary,
  surfaceMuted,
  dividerInner,
  iconSize,
  motionFadeUp,
} from '../../../lib/design-system';
import { cn } from '../../../lib/utils';

const inputClasses =
  "w-full rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1/50 focus:border-transparent p-3 text-sm";

const btnPrimary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const btnSecondary =
  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const AIGenerationModal = ({
  isOpen,
  onClose,
  onGenerate,
  courseData,
  bloomLevels,
  getCourseOutcomes,
  formatOutcomeForDisplay,
  extractOutcomeCode,
  generating,
  aiError,
}) => {
  const [numQuestions, setNumQuestions] = useState(5);
  const [selectedBloomLevel, setSelectedBloomLevel] = useState('');
  const [selectedCourseOutcome, setSelectedCourseOutcome] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  if (!isOpen) return null;

  const handleGenerate = () => {
    if (!selectedCourseOutcome || !selectedBloomLevel) {
      alert("Please select a course outcome and Bloom's taxonomy level.");
      return;
    }
    onGenerate({
      numQuestions,
      selectedBloomLevel,
      selectedCourseOutcome,
      additionalContext,
    });
  };

  const handleCancel = () => {
    setNumQuestions(5);
    setSelectedBloomLevel('');
    setSelectedCourseOutcome('');
    setAdditionalContext('');
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={handleCancel}
      />

      {/* Panel */}
      <motion.div
        {...motionFadeUp}
        className={cn(
          card,
          "relative w-full max-w-2xl max-h-[95vh] flex flex-col shadow-xl mx-4"
        )}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between p-6 border-b", dividerInner)}>
          <div className="flex items-center gap-3">
            <Sparkles className={cn("text-accent1", iconSize.lg)} />
            <h2 className={cn(cardTitle, "text-lg")}>Generate Questions with AI</h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
            aria-label="Close modal"
          >
            <X className={cn("text-gray-500 dark:text-gray-400", iconSize.md)} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Number of Questions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number of Questions
                </label>
                <input
                  type="number"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(Number(e.target.value))}
                  min="1"
                  max="20"
                  className={inputClasses}
                />
              </div>

              {/* Bloom Taxonomy Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bloom Taxonomy Level <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBloomLevel}
                  onChange={(e) => setSelectedBloomLevel(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Select level</option>
                  {bloomLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Course Outcome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Course Outcome <span className="text-red-500">*</span>
              </label>
              {getCourseOutcomes().length > 0 ? (
                <select
                  value={selectedCourseOutcome}
                  onChange={(e) => setSelectedCourseOutcome(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Select course outcome</option>
                  {getCourseOutcomes().map((outcome, index) => {
                    const displayText = formatOutcomeForDisplay(outcome);
                    const code = extractOutcomeCode(outcome);
                    return (
                      <option key={index} value={code}>
                        {displayText}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="w-full p-3 border border-yellow-300 dark:border-yellow-700 rounded-sm bg-yellow-50 dark:bg-yellow-900/20">
                  <p className={cn(bodySecondary, "text-yellow-800 dark:text-yellow-400")}>
                    No course outcomes found. Please add course outcomes in course settings.
                  </p>
                </div>
              )}
            </div>

            {/* Additional Context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Context (Optional)
              </label>
              <textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                className={inputClasses}
                rows="3"
                placeholder="Provide any additional context..."
              />
            </div>

            {/* Error block */}
            {aiError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                <p className="text-sm text-red-600 dark:text-red-400">{aiError}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={cn(
            "flex justify-end gap-3 p-6 border-t",
            surfaceMuted,
            dividerInner
          )}
        >
          <button onClick={handleCancel} className={btnSecondary}>
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !numQuestions || !selectedCourseOutcome || !selectedBloomLevel}
            className={btnPrimary}
          >
            {generating ? (
              <>
                <div className="h-4 w-4 rounded-sm bg-white/50 animate-pulse" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className={iconSize.md} />
                Generate Questions
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default AIGenerationModal;
